import React from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SideDrawer from './common/SideDrawer';
import PreviewSampleTables from './PreviewSampleTables';
import type { TransformationTestResponse, HealthIssue } from '../services/api';

export interface DataPreviewModalProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  error: string | null;
  data: TransformationTestResponse | null;
  notice: string | null;
  nodeLabel: string | null;
  onRefresh?: () => void;
  violations?: HealthIssue[];
}

const DataPreviewModal: React.FC<DataPreviewModalProps> = ({
  open,
  onClose,
  loading,
  error,
  data,
  notice,
  nodeLabel,
  onRefresh,
  violations,
}) => {
  const title = (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="h6" fontWeight={700}>
        Data Preview
      </Typography>
      {nodeLabel && (
        <Typography variant="body2" color="text.secondary">
          — {nodeLabel}
        </Typography>
      )}
    </Stack>
  );

  const actions = onRefresh ? (
    <Tooltip title="Refresh Data">
      <IconButton size="small" onClick={onRefresh}>
        <RefreshIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  ) : undefined;

  return (
    <SideDrawer
      open={open}
      onClose={onClose}
      title={title}
      actions={actions}
      defaultWidth="min(72vw, 1200px)"
    >
      <Box sx={{ flex: 1, overflow: 'auto', p: 2, scrollbarGutter: 'stable' }}>
        {error && <Alert severity="error">{error}</Alert>}

        {notice && !error && (
          <Alert severity="info" sx={{ mb: 1 }}>
            {notice}
          </Alert>
        )}

        {loading && (
          <Box p={4} display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        )}

        {!loading && !error && !data && (
          <Box
            p={4}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Data Preview
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select a node on the canvas to inspect its data.
            </Typography>
          </Box>
        )}

        {!loading && data && Object.keys(data.sample_output || {}).length === 0 && (
          <Box
            p={4}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Typography variant="subtitle1" color="text.primary" gutterBottom>
              No Rows Returned
            </Typography>
            {data.dependent_sources?.length > 0 ? (
              <Typography variant="body2" color="warning.main">
                Transformation logic may have filtered out all rows.
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Source tables appear to be empty.
              </Typography>
            )}
          </Box>
        )}

        {!loading && data && Object.keys(data.sample_output || {}).length > 0 && (
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="caption" color="text.secondary">
                {Object.values(data.sample_output || {}).reduce(
                  (total, rows) => total + (Array.isArray(rows) ? rows.length : 0),
                  0,
                )}{' '}
                row sample · {data.dependent_sources.length} sources involved
              </Typography>
            </Stack>
            <PreviewSampleTables sampleOutput={data.sample_output} violations={violations} />
          </Box>
        )}
      </Box>
    </SideDrawer>
  );
};

export default DataPreviewModal;
